import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetAllDocumentsRequestList,
  useGetprovidersListForFilters,
  UseGetRfiListForFilters,
  useRiskSummariesByIds,
} from "../custom-hooks/queries";
import useRequestListStickyFilters from "../custom-hooks/useRequestListStickyFilters";
import { extractRiskRowIds } from "../utils/riskSummaryGridUtils";
import { useCanSeeRiskSummaryColumn } from "../utils/orgFeatureGates";
import SkeletonLoader from "../components/SkeletonLoader";
import Pagination from "../components/CustomPagination";
import Notification from "../components/Notification";
import { useDispatch, useSelector } from "react-redux";
import { setSearchTextByPage } from "../redux-store/features/providerFiltersSlice";
import PageToolbarFilters from "../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../components/Filters/ToolbarSearch";
import { generateV2ServiceCategoryOptions } from "../utils/serviceCategoryV2Mapping";
import TableExportMenu from "../components/Menus/TableExportMenu";
import { VQA_LIBRARY_REQUESTS_LABEL } from "../utils/vqaNavigationLabels";
import { documentsCellStyles } from "../utils/exportBadgeStyles";
import {
  buildLibraryRequestsExportData,
  getLibraryRequestsTableRows,
  LIBRARY_REQUESTS_DEFAULT_SORT_CONFIG,
  LIBRARY_REQUESTS_USE_SERVER_SORT,
  sortConfigToLibraryRequestsApiParams,
} from "../utils/libraryRequestsGridMapper";
import LibraryRequestsGridTable from "../components/LibraryRequests/LibraryRequestsGridTable";
import useOrgVqaOnlyStatus from "../custom-hooks/useOrgVqaOnlyStatus";
import { libraryRequestTypeOptions, libraryDocumentTypes } from "../utils/vqaOnlyOrg";
import { REQUEST_LIST_SCREEN_KEYS } from "../utils/requestListStickyFilterPersistence";
import { getLibraryRequestStatusFilterColumn } from "../utils/libraryRequestStatusFilterOptions";

const SPONSOR_LIBRARY_EMPTY_SELECTED_IDS = {
  docType: [],
  provider: [],
  status: [],
  services: [],
};

export const SponsorVQAGrid = () => {
  const [show, setShow] = useState(false);

  const { data: providers } = useGetprovidersListForFilters();
  const { data: rfis } = UseGetRfiListForFilters();
  // Until the profile says, the list leaves VQQ requests out rather than show them to a VQA only org.
  const isVqaOnly = useOrgVqaOnlyStatus() !== false;

  const v2ServiceOptions = useMemo(
    () => generateV2ServiceCategoryOptions(rfis || []),
    [rfis]
  );

  const dispatch = useDispatch();

  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["myDocuments"]
    ) || "";

  const {
    selectedIds,
    setSelectedIds,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    sortFetchNonce,
  } = useRequestListStickyFilters({
    screenKey: REQUEST_LIST_SCREEN_KEYS.SPONSOR_LIBRARY_REQUESTS,
    emptySelectedIds: SPONSOR_LIBRARY_EMPTY_SELECTED_IDS,
    defaultSortConfig: LIBRARY_REQUESTS_DEFAULT_SORT_CONFIG,
    searchQuery,
    useServerSort: LIBRARY_REQUESTS_USE_SERVER_SORT,
  });

  const filters = useMemo(
    () => [
      {
        id: "provider",
        name: "Vendor",
        searchable: true,
        options: providers || [],
        total: 1335,
      },
      {
        id: "docType",
        name: "Document Type",
        searchable: false,
        options: libraryRequestTypeOptions(),
        total: libraryRequestTypeOptions().length,
      },
      {
        id: "services",
        name: "Scope",
        searchable: true,
        isV2: true,
        options: v2ServiceOptions,
        total: v2ServiceOptions?.length || 0,
      },
      getLibraryRequestStatusFilterColumn(),
    ],
    [providers, v2ServiceOptions]
  );

  const serviceNames = useMemo(() => {
    return (
      selectedIds.services?.map((serviceId) => {
        const service = rfis?.find(
          (rfi) =>
            rfi.rfi_id === serviceId ||
            rfi.id === serviceId ||
            rfi.hash_id === serviceId
        );
        return (
          service?.services ||
          service?.rfi_name ||
          service?.name ||
          serviceId
        );
      }) || []
    );
  }, [selectedIds.services, rfis]);
  const rfiIds = [];

  const serverSortParams = useMemo(() => {
    if (!LIBRARY_REQUESTS_USE_SERVER_SORT) return undefined;
    const { orderBy, sortDirection } =
      sortConfigToLibraryRequestsApiParams(sortConfig);
    if (!orderBy || !sortDirection) return undefined;
    return { orderBy, sortDirection };
  }, [sortConfig]);

  const {
    data: requestList,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllDocumentsRequestList(
    currentPage,
    libraryDocumentTypes(selectedIds.docType, isVqaOnly),
    selectedIds.provider,
    selectedIds.status,
    rfiIds,
    [],
    searchQuery,
    undefined,
    serviceNames,
    serverSortParams,
    sortFetchNonce
  );

  const sortedItems = useMemo(
    () =>
      getLibraryRequestsTableRows(
        requestList,
        sortConfig,
        LIBRARY_REQUESTS_USE_SERVER_SORT,
        "sponsor"
      ),
    [requestList, sortConfig]
  );

  const exportDataRef = useRef(null);

  // 14029 — Pre-fetch published risk summaries for the visible page (export only
  // emits a Risk Summary cell where a published snapshot exists). A sponsor whose
  // grid has no Risk Summary column gets no such column in the export either.
  const showRisk = useCanSeeRiskSummaryColumn();
  const exportRowIds = useMemo(() => extractRiskRowIds(sortedItems), [sortedItems]);
  const { data: exportRiskSummariesRaw } = useRiskSummariesByIds(exportRowIds, {
    enabled: showRisk && exportRowIds.length > 0,
  });
  const exportRiskSummaries = exportRiskSummariesRaw ?? {};

  useEffect(() => {
    exportDataRef.current = () =>
      buildLibraryRequestsExportData(sortedItems, {
        audience: "sponsor",
        summaries: exportRiskSummaries,
        showRisk,
      });
  }, [sortedItems, exportRiskSummaries, showRisk]);

  return (
    <div className="w-full min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 pb-6">
      <div className="shadow-header -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="bg-white py-4 flex flex-col gap-4 px-4 sm:px-6 lg:px-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="sm:flex-auto min-w-0">
            <h1 className="text-lg font-medium text-gray-900">
              {VQA_LIBRARY_REQUESTS_LABEL}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              The table below displays all bookmarked and requested Audit Reports from auditor-led qualification assessments in the Diligent document library.
              {isVqaOnly ? "" : " VQQs are listed under My VQQs."}
            </p>
          </div>
          <div className="flex shrink-0 justify-end mt-4">
            <TableExportMenu
              getData={() =>
                exportDataRef.current
                  ? exportDataRef.current()
                  : { records: [], headers: [] }
              }
              reportTitle={VQA_LIBRARY_REQUESTS_LABEL}
              reportSubtitle="Documents requested from your vendors"
              filePrefix="Library_Requests"
              downloadType={VQA_LIBRARY_REQUESTS_LABEL}
              cellStyles={documentsCellStyles}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6 w-full min-w-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between w-full min-w-0">
          <div className="flex w-full min-w-0 flex-1 flex-col gap-3 lg:max-w-xl">
            <ToolbarSearch
              value={searchQuery}
              onDebouncedChange={(text) =>
                dispatch(
                  setSearchTextByPage({
                    pageKey: "myDocuments",
                    searchText: text,
                  })
                )
              }
              placeholder="Search documents..."
            />
          </div>
          <div className="w-full min-w-0 lg:w-auto lg:flex-shrink-0">
            <PageToolbarFilters
              sections={filters}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              serviceCategoryV2SourceData={rfis || []}
            />
          </div>
        </div>
      </div>

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="px-4 py-6 sm:px-6">
            <SkeletonLoader />
          </div>
        ) : (
            <LibraryRequestsGridTable
              data={sortedItems}
              sortConfig={sortConfig}
              onSort={handleSort}
            refetch={refetch}
            isSearchActive={!!searchQuery}
          />
        )}

        {!isError &&
          !isLoading &&
          sortedItems &&
          sortedItems.length > 0 && (
            <div className="border-t border-gray-200">
              <Pagination
                totalPages={
                  requestList?.document_request_for_sponsor?.total_pages ||
                  requestList?.document_request_for_provider?.total_pages ||
                  1
                }
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
      </div>

      <Notification
        show={show}
        setShow={setShow}
        errorMessage={error && error.message}
        status="error"
      />
    </div>
  );
};
